import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { CheckCircle, XCircle, ArrowsClockwise, Clock, ArrowSquareOut, Sparkle, Rss } from "@phosphor-icons/react";
import SourceToStoryPanel from "./SourceToStoryPanel";
import BulkImportPanel from "./BulkImportPanel";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

function TypeChip({ t }) {
  return (
    <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-black/5 border border-black/10 text-[#151312]">
      {(t || "story").replace(/_/g, " ")}
    </span>
  );
}

function StoryRow({ s, onDecide, onCompose }) {
  const [busy, setBusy] = useState(false);
  const decide = async (status) => {
    setBusy(true);
    try { await onDecide(s.id, status); } finally { setBusy(false); }
  };
  const compose = async () => {
    setBusy(true);
    try { await onCompose(s.id); } finally { setBusy(false); }
  };
  const loc = [s.location?.city, s.location?.state, s.location?.country].filter(Boolean).join(", ");
  return (
    <div className="rounded-xl border border-black/15 bg-black/5 p-4 flex flex-col gap-2" data-testid={`news-row-${s.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <TypeChip t={s.story_type} />
        <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]/70">{s.category}</span>
        <span className="ml-auto font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]/70">{s.moderation_status}</span>
      </div>
      <div className="font-serif-display text-lg leading-tight text-[#151312]">{s.title || s.headline}</div>
      {s.summary && <div className="text-sm text-[#3A3830]/85 line-clamp-2">{s.summary}</div>}
      <div className="text-xs text-[#3A3830]/80 flex flex-wrap gap-x-3 gap-y-0.5">
        {s.publisher && <span>· {s.publisher}</span>}
        {loc && <span>· {loc}</span>}
        {s.publication_date && <span>· {new Date(s.publication_date).toISOString().slice(0, 10)}</span>}
        {s.discovered_via && <span>· {s.discovered_via}</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {["approved", "rejected", "archived", "pending"].map((st) => (
          <button
            key={st}
            disabled={busy || s.moderation_status === st}
            onClick={() => decide(st)}
            data-testid={`news-decide-${s.id}-${st}`}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 border font-mono-tech text-[9px] tracking-[0.22em] uppercase disabled:opacity-40 ${
              st === "approved" ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" :
              st === "rejected" ? "border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100" :
              st === "archived" ? "border-stone-400 bg-stone-50 text-stone-800 hover:bg-stone-100" :
              "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
            }`}
          >
            {st === "approved" && <CheckCircle size={11} weight="fill" />}
            {st === "rejected" && <XCircle size={11} weight="fill" />}
            {st === "archived" && <ArrowsClockwise size={11} weight="bold" />}
            {st === "pending" && <Clock size={11} weight="fill" />}
            {st}
          </button>
        ))}
        {s.story_type === "news_link" && !s.composed_by_ai && (
          <button
            disabled={busy}
            onClick={compose}
            data-testid={`news-compose-${s.id}`}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] bg-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
          >
            <Sparkle size={11} weight="fill" /> AI compose
          </button>
        )}
        {s.canonical_url && (
          <a
            href={s.canonical_url} target="_blank" rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 text-[#151312] bg-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
          >
            <ArrowSquareOut size={11} weight="bold" /> Source
          </a>
        )}
      </div>
    </div>
  );
}

const TYPE_FILTERS = [
  { key: "", label: "All types" },
  { key: "news_link", label: "News" },
  { key: "observation", label: "Observation" },
  { key: "photo_story", label: "Photo" },
  { key: "personal_story", label: "Personal" },
  { key: "nature_memory", label: "Memory" },
  { key: "research", label: "Research" },
  { key: "video", label: "Video" },
  { key: "question", label: "Question" },
  { key: "environmental_warning", label: "Warning" },
];

export default function NewsTab() {
  const [tab, setTab] = useState("pending");
  const [storyType, setStoryType] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [busyDiscover, setBusyDiscover] = useState(false);
  const [busyFullSync, setBusyFullSync] = useState(false);
  const [busyCluster, setBusyCluster] = useState(false);
  const [busyHealth, setBusyHealth] = useState(false);
  const [busyDiscoveryAgent, setBusyDiscoveryAgent] = useState(false);
  const [busyMigrate, setBusyMigrate] = useState(false);
  const [busySeed, setBusySeed] = useState(false);
  const [busyIllustrate, setBusyIllustrate] = useState(false);
  const PAGE = 30;

  const load = useCallback(async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await http.get("/nature/stories/list/all", {
        params: {
          status: tab, limit: PAGE, offset: nextOffset,
          story_type: storyType || undefined,
        },
      });
      setItems((prev) => append ? [...prev, ...(data.stories || [])] : (data.stories || []));
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } finally { setLoading(false); }
  }, [tab, storyType]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await http.get("/nature/stories/stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const decide = async (id, status) => {
    try {
      await http.post(`/nature/stories/${id}/moderate`, { status });
      toast.success(`Marked ${status}`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const compose = async (id) => {
    try {
      const { data } = await http.post("/nature/stories/compose", { story_id: id });
      toast.success(`AI article composed: "${(data.title || "").slice(0, 60)}…"`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Compose failed"); }
  };

  const runDiscover = async () => {
    setBusyDiscover(true);
    try {
      const { data } = await http.post("/nature/stories/discover/tick", null, { params: { feed_limit: 8, per_feed_limit: 3 } });
      const feedsAdded = (data.feeds || []).filter((f) => f.added > 0);
      if (data.ingested > 0) {
        toast.success(`Discovered ${data.ingested} new items across ${feedsAdded.length} feed${feedsAdded.length === 1 ? "" : "s"}`);
      } else {
        toast.message("No new items", { description: `Polled ${data.feeds?.length || 0} feeds — everything was already indexed. Try again later.` });
      }
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); }
    finally { setBusyDiscover(false); }
  };
  const runFullSync = async () => {
    if (!window.confirm("Full sync walks every RSS source (~50 feeds, ~10 items each). It may take 30–90s and can add hundreds of news items. Continue?")) return;
    setBusyFullSync(true);
    try {
      const { data } = await http.post("/nature/stories/discover/full-sync", null, { params: { per_feed_limit: 10 } });
      const feedsAdded = (data.feeds || []).filter((f) => f.added > 0);
      toast.success(`Full sync ingested ${data.ingested} items from ${feedsAdded.length}/${data.feeds?.length || 0} feeds`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Full sync failed"); }
    finally { setBusyFullSync(false); }
  };
  const runCluster = async () => {
    setBusyCluster(true);
    try {
      const { data } = await http.post("/nature/stories/events/cluster/tick", null, { params: { hours: 168 } });
      if (data.clusters_created > 0) {
        toast.success(`Grouped ${data.stories_attached} stories into ${data.clusters_created} event clusters`);
      } else {
        toast.message("No new clusters found", { description: `Scanned ${data.scanned} recent stories — no duplicate events detected in the last week.` });
      }
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cluster tick failed"); }
    finally { setBusyCluster(false); }
  };
  const runHealth = async () => {
    setBusyHealth(true);
    try {
      const { data } = await http.post("/nature/stories/health/tick", null, { params: { limit: 40 } });
      const by = {};
      for (const r of data.results || []) by[r.link_status] = (by[r.link_status] || 0) + 1;
      const summary = Object.entries(by).map(([k, v]) => `${v} ${k}`).join(" · ") || "no due URLs";
      toast.success(`Checked ${data.checked} URLs — ${summary}`);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Health check failed"); }
    finally { setBusyHealth(false); }
  };
  const runDiscoveryAgent = async () => {
    if (!window.confirm(
      "Run the News Discovery Agent? Walks every RSS + Google-News feed and re-writes fresh items with a 75-200 word AI summary. Runs in the background — you'll see the new items in the feed within ~60s.",
    )) return;
    setBusyDiscoveryAgent(true);
    try {
      const { data } = await http.post("/nature/discovery/run", null, {
        params: { per_feed_limit: 4, enrich_limit: 30 },
      });
      toast.success(`Discovery agent started (run ${data?.run_id || "queued"}) — refresh the feed in ~60s.`);
      setTimeout(() => { load(); loadStats(); }, 60_000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery agent failed"); }
    finally { setBusyDiscoveryAgent(false); }
  };
  const runMigrate = async () => {
    setBusyMigrate(true);
    try {
      const { data } = await http.post("/nature/stories/migrate-observations");
      toast.success(`Migrated ${data.migrated} · skipped ${data.skipped}`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Migration failed"); }
    finally { setBusyMigrate(false); }
  };
  const runSeed = async (force = false) => {
    if (!window.confirm(force
      ? "Force re-seed 12 fresh AI sample stories? (Existing samples remain.)"
      : "Generate 12 AI sample stories to populate /stories? (Only runs once per 90 days unless forced.)"
    )) return;
    setBusySeed(true);
    try {
      const { data } = await http.post("/nature/stories/seed-samples", null, force ? { params: { force: 1 } } : undefined);
      if (data.skipped) toast.message("Skipped", { description: data.reason });
      else toast.success(`Seeded ${data.seeded} sample stories`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setBusySeed(false); }
  };
  // Iteratively call illustrate-samples in small batches so the ingress
  // never times out (Nano Banana ~10s/image; 3 per batch stays under 60s).
  const runIllustrate = async () => {
    if (!window.confirm("Generate Nano Banana cover images for all sample stories that don't yet have one? Runs in batches of 3.")) return;
    setBusyIllustrate(true);
    let total = 0, batches = 0;
    try {
      while (batches < 10) {
        batches += 1;
        const { data } = await http.post("/nature/stories/illustrate-samples", null, { params: { limit: 3 }, timeout: 90000 });
        total += data.succeeded || 0;
        if (!data.attempted) break;
        toast.message(`Batch ${batches}`, { description: `${data.succeeded}/${data.attempted} covered so far · ${total} total` });
      }
      toast.success(`Illustrated ${total} sample covers across ${batches} batch${batches===1?"":"es"}`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Illustration failed"); }
    finally { setBusyIllustrate(false); }
  };
  // News illustrator — same batched pattern, targets news_link items instead.
  const runIllustrateNews = async () => {
    if (!window.confirm("Generate Nano Banana covers for the newest news items without an image? Runs in batches of 3.")) return;
    setBusyIllustrate(true);
    let total = 0, batches = 0;
    try {
      while (batches < 10) {
        batches += 1;
        const { data } = await http.post("/nature/stories/illustrate-news", null, { params: { limit: 3 }, timeout: 90000 });
        total += data.succeeded || 0;
        if (!data.attempted) break;
        toast.message(`News batch ${batches}`, { description: `${data.succeeded}/${data.attempted} covered · ${total} total` });
      }
      toast.success(`Illustrated ${total} news covers`);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "News illustration failed"); }
    finally { setBusyIllustrate(false); }
  };

  return (
    <div className="space-y-5" data-testid="news-tab">
      <SourceToStoryPanel />
      <BulkImportPanel />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: "Approved", v: stats.approved },
            { k: "Pending", v: stats.pending },
            { k: "Broken links", v: stats.broken },
            { k: "Events", v: stats.events },
          ].map((s) => (
            <div key={s.k} className="rounded-xl border border-black/15 bg-white p-3">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/70">{s.k}</div>
              <div className="font-serif-display text-3xl text-[#151312]">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`news-tab-${t.key}`}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 border font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
              tab === t.key ? "bg-[#E4A15C] text-[#151312] border-[#E4A15C]" : "border-black/15 text-[#151312] bg-white hover:bg-black/5"
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2 flex-wrap">
          <button onClick={runDiscover} disabled={busyDiscover} data-testid="news-discover" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-50">
            <Rss size={11} weight="bold" /> {busyDiscover ? "Discovering…" : "Discover RSS"}
          </button>
          <button onClick={runFullSync} disabled={busyFullSync} data-testid="news-full-sync" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:opacity-90 disabled:opacity-50">
            <Rss size={11} weight="bold" /> {busyFullSync ? "Syncing…" : "Full sync all feeds"}
          </button>
          <button onClick={runDiscoveryAgent} disabled={busyDiscoveryAgent} data-testid="news-discovery-agent" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[#151312] bg-[#151312] text-[#F5EBDC] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:opacity-90 disabled:opacity-50">
            <Rss size={11} weight="bold" /> {busyDiscoveryAgent ? "Running…" : "Discovery + AI"}
          </button>
          <button onClick={runCluster} disabled={busyCluster} data-testid="news-cluster" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-50">
            {busyCluster ? "Clustering…" : "Cluster events"}
          </button>
          <button onClick={runHealth} disabled={busyHealth} data-testid="news-health" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-50">
            {busyHealth ? "Checking…" : "URL health"}
          </button>
          <button onClick={runMigrate} disabled={busyMigrate} data-testid="news-migrate-obs" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-50">
            {busyMigrate ? "Migrating…" : "Migrate observations"}
          </button>
          <button onClick={() => runSeed(false)} disabled={busySeed} data-testid="news-seed-samples" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-50">
            <Sparkle size={11} weight="fill" /> {busySeed ? "Seeding…" : "AI seed 12"}
          </button>
          <button onClick={runIllustrate} disabled={busyIllustrate} data-testid="news-illustrate-samples" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[#E4A15C] bg-[#151312] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[#E4A15C] hover:text-[#151312] disabled:opacity-50">
            <Sparkle size={11} weight="fill" /> {busyIllustrate ? "Illustrating…" : "AI illustrate samples"}
          </button>
          <button onClick={runIllustrateNews} disabled={busyIllustrate} data-testid="news-illustrate-news" className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[#E4A15C] bg-white text-[#151312] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[#151312] hover:text-[#E4A15C] disabled:opacity-50">
            <Sparkle size={11} weight="fill" /> {busyIllustrate ? "Illustrating…" : "AI illustrate news"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-[10px] tracking-widest text-[#3A3830]/70">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-black/15 p-8 text-center text-[#3A3830]/75">
          Nothing in this tab yet.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 -mt-2">
            <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]/70 mr-1">Type</span>
            {TYPE_FILTERS.map((t) => (
              <button
                key={t.key || "any"}
                onClick={() => setStoryType(t.key)}
                data-testid={`news-type-${t.key || "any"}`}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border font-mono-tech text-[9px] tracking-[0.22em] uppercase ${
                  storyType === t.key
                    ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                    : "border-black/15 text-[#151312] bg-white hover:bg-black/5"
                }`}
              >{t.label}</button>
            ))}
            <span className="ml-auto font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]/70">{items.length} / {total}</span>
          </div>
          <div className="space-y-3">
            {items.map((s) => <StoryRow key={s.id} s={s} onDecide={decide} onCompose={compose} />)}
          </div>
          {items.length < total && (
            <div className="text-center">
              <button
                onClick={() => load(offset + PAGE, true)}
                data-testid="news-load-more"
                className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5"
              >Load more — {total - items.length} left</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
